// src/hooks/useNumbersGame.js
import { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

// Error boundary for Firebase operations
class FirebaseError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
    this.name = "FirebaseError";
  }
}

const useNumbersGame = (gameType = 'arabicNumberSpeed') => {
  const [userId, setUserId] = useState(null);
  const [level, setLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(1);
  const [numbersData, setNumbersData] = useState([]);
  const [bestScores, setBestScores] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user progress data
  const loadUserData = useCallback(async (uid) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        // User doesn't exist yet, create with default data
        const defaultData = {
          progress: {
            arabicNumberSpeed: {
              currentLevel: 1,
              maxLevel: 1,
              levels: {}
            }
          }
        };
        await setDoc(doc(db, 'users', uid), defaultData);
        setLevel(1);
        setMaxLevel(1);
        setBestScores({});
        return;
      }

      const data = userDoc.data();
      const gameData = data.progress?.[gameType] || {};
      
      setLevel(gameData.currentLevel || 1);
      setMaxLevel(gameData.maxLevel || 1);
      setBestScores(gameData.levels || {});

    } catch (error) {
      console.error('Error loading user data:', error);
      setError(error);
      // Fallback to local storage if available
      const localData = localStorage.getItem('gameProgress_arabicNumberSpeed');
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          setLevel(parsed.level || 1);
          setMaxLevel(parsed.maxLevel || 1);
          setBestScores(parsed.bestScores || {});
        } catch (e) {
          console.error('Error parsing local storage data:', e);
          // Set defaults if parsing fails
          setLevel(1);
          setMaxLevel(1);
          setBestScores({});
        }
      } else {
        // Set defaults if no local data
        setLevel(1);
        setMaxLevel(1);
        setBestScores({});
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load numbers data from Firebase
  const loadNumbersData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const snapshot = await getDocs(collection(db, 'numbers'));
      
      const data = [];
      snapshot.forEach(doc => {
        const docData = doc.data();
        // Normalize the data structure
        const normalizedData = {
          englishNumeral: parseInt(docData.englishNumeral || docData.englishNumber || docData.number) || 0,
          arabicNumeral: docData.arabicNumeral || docData.arabicNumber || '',
          arabicWord: docData.arabicWord || docData.word || '',
          pronunciation: docData.pronunciation || '',
          audioURL: docData.audioURL || docData.audio || ''
        };
        data.push(normalizedData);
      });
      
      // Sort by English numeral
      data.sort((a, b) => a.englishNumeral - b.englishNumeral);
      setNumbersData(data);
      
      // Cache data in local storage
      localStorage.setItem('gameNumbers_arabicNumberSpeed', JSON.stringify(data));
      
      return data;
    } catch (error) {
      console.error('Error loading numbers data:', error);
      setError(error);
      
      // Fallback to local storage
      const localData = localStorage.getItem('gameNumbers_arabicNumberSpeed');
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          setNumbersData(parsed);
          return parsed;
        } catch (e) {
          console.error('Error parsing local numbers data:', e);
        }
      }
      
      // If no data available, create fallback data
      const fallbackData = [];
      for (let i = 0; i <= 200; i++) {
        fallbackData.push({
          englishNumeral: i,
          arabicNumeral: String(i),
          arabicWord: `Number ${i}`,
          pronunciation: '',
          audioURL: ''
        });
      }
      setNumbersData(fallbackData);
      return fallbackData;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save level completion data
  const saveLevelData = useCallback(async (levelNum, scoreData) => {
    if (!userId) {
      // Save to local storage if not authenticated
      const localData = {
        level: levelNum,
        maxLevel: Math.max(levelNum, maxLevel),
        bestScores: {
          ...bestScores,
          [`level${levelNum}`]: scoreData
        }
      };
      localStorage.setItem(`gameProgress_${gameType}`, JSON.stringify(localData));
      
      // Update local state
      setBestScores(prev => ({
        ...prev,
        [`level${levelNum}`]: scoreData
      }));
      setMaxLevel(prev => Math.max(prev, levelNum));
      return true;
    }
    
    const userRef = doc(db, 'users', userId);
    const levelKey = `level${levelNum}`;
    
    try {
      const currentBest = bestScores[levelKey] || {};
      const currentBestScore = currentBest.bestScore || currentBest.totalScore || 0;
      const isNewBest = !currentBest || scoreData.totalScore > currentBestScore;
      
      const dataToSave = {
        ...scoreData,
        bestScore: isNewBest ? scoreData.totalScore : currentBestScore,
        bestStars: isNewBest ? scoreData.stars : Math.max(scoreData.stars, currentBest.stars || 0),
        bestAccuracy: isNewBest ? scoreData.accuracy : Math.max(scoreData.accuracy, currentBest.accuracy || 0),
        completedAt: new Date().toISOString(),
        latestScore: scoreData.totalScore,
        latestStars: scoreData.stars,
        latestAccuracy: scoreData.accuracy
      };
      
      await setDoc(userRef, {
        progress: {
          [gameType]: {
            currentLevel: Math.max(levelNum, level),
            maxLevel: Math.max(levelNum, maxLevel),
            levels: {
              ...bestScores,
              [levelKey]: dataToSave
            }
          }
        }
      }, { merge: true });
      
      // Update local state
      setBestScores(prev => ({
        ...prev,
        [levelKey]: dataToSave
      }));
      
      setMaxLevel(prev => Math.max(prev, levelNum));
      
      return true;
    } catch (error) {
      console.error('Error saving level data:', error);
      return false;
    }
  }, [userId, level, maxLevel, bestScores]);

  // Update current level
  const updateLevel = useCallback((newLevel) => {
    if (newLevel <= maxLevel && newLevel >= 1) {
      setLevel(newLevel);
      return true;
    }
    return false;
  }, [maxLevel]);

  // Unlock next level
  const unlockNextLevel = useCallback(() => {
    const nextLevel = level + 1;
    // For numbers game, we allow up to level 7
    if (nextLevel <= 7) {
      setMaxLevel(Math.max(maxLevel, nextLevel));
      setLevel(nextLevel);
      return true;
    }
    return false;
  }, [level, maxLevel]);

  // Get best score for a level
  const getBestScore = useCallback((levelNum) => {
    return bestScores[`level${levelNum}`] || null;
  }, [bestScores]);

  // Check if level is unlocked
  const isLevelUnlocked = useCallback((levelNum) => {
    return levelNum <= maxLevel;
  }, [maxLevel]);

  // Reset all progress
  const resetAllProgress = useCallback(async () => {
    try {
      if (userId) {
          await setDoc(doc(db, 'users', userId), {
            progress: {
              [gameType]: {
                currentLevel: 1,
                maxLevel: 1,
                levels: {}
              }
            }
          }, { merge: true });
      }
      
      // Clear local storage
      localStorage.removeItem(`gameProgress_${gameType}`);
      
      // Reset state
      setLevel(1);
      setMaxLevel(1);
      setBestScores({});
      
      return true;
    } catch (error) {
      console.error('Error resetting progress:', error);
      return false;
    }
  }, [userId]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
        loadUserData(user.uid);
      } else {
        setUserId(null);
        // Load from local storage for guest users
        const localData = localStorage.getItem(`gameProgress_${gameType}`);
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            setLevel(parsed.level || 1);
            setMaxLevel(parsed.maxLevel || 1);
            setBestScores(parsed.bestScores || {});
          } catch (e) {
            console.error('Error parsing local storage data:', e);
            setLevel(1);
            setMaxLevel(1);
            setBestScores({});
          }
        } else {
          setLevel(1);
          setMaxLevel(1);
          setBestScores({});
        }
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, [loadUserData]);

  // Load numbers data on mount
  useEffect(() => {
    loadNumbersData();
  }, [loadNumbersData]);

  return {
    // State
    userId,
    level,
    setLevel,
    maxLevel,
    numbersData,
    bestScores,
    isLoading,
    error,
    
    // Actions
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    getBestScore,
    isLevelUnlocked,
    resetAllProgress,
    
    // Utilities
    reloadUserData: () => userId && loadUserData(userId),
    reloadNumbersData: loadNumbersData
  };
};

export default useNumbersGame;